import { AudienceModeId } from "./audienceModes";

export type SignalCategory =
  | "Hiring and Workforce"
  | "Contact and Access"
  | "Growth and Expansion"
  | "Location and Travel"
  | "Reputation and Trust"
  | "Website and Digital Presence"
  | "Partnership and Outreach"
  | "Warnings and Risks"
  | "AI and Automation";

export type SignalConfidence = "Basic" | "Improved" | "Full";

export interface SignalDefinition {
  key: string;
  defaultLabel: string;
  category: SignalCategory;
  defaultSource: string;
  defaultConfidence: SignalConfidence;
  filterable: boolean;
  rankingEligible: boolean;
  cardEligible: boolean;
  detailEligible: boolean;
  audienceLabels?: Partial<Record<AudienceModeId, string>>;
  audienceDescriptions?: Partial<Record<AudienceModeId, string>>;
  audienceWeights?: Partial<Record<AudienceModeId, number>>;
}

export const CANONICAL_SIGNALS: Record<string, SignalDefinition> = {
  // --- 1. Hiring and Workforce ---
  currently_hiring: {
    key: "currently_hiring",
    defaultLabel: "Currently Hiring",
    category: "Hiring and Workforce",
    defaultSource: "Google Maps Listing / Status",
    defaultConfidence: "Basic",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Currently Hiring",
      recruiter: "Active Recruitment Openings",
      staffing_agency: "Staffing Demand Active",
      career_coach: "Open Positions for Clients",
      workforce_organization: "Community Hiring Partner",
      college_career_center: "Student Hiring Employer",
      sales_professional: "Operational Expansion Signal",
      business_development: "Active Team Growth",
      agency_freelancer: "Hiring & Expansion Signal"
    },
    audienceDescriptions: {
      job_seeker: "Active open openings or currently hiring confirmation in local operations.",
      recruiter: "Confirmed active openings or recent hiring postings requiring talent sourcing.",
      staffing_agency: "Active workforce demand suitable for contract, temp-to-hire, or direct placement.",
      career_coach: "Confirmed active job openings suitable for placement and career transition clients.",
      workforce_organization: "Active local employer with immediate hiring needs for program participants.",
      college_career_center: "Employer actively recruiting graduating students or entry-level talent.",
      sales_professional: "Active hiring indicates operational spending and budget for new initiatives.",
      business_development: "Team expansion signal creating new partnership and vendor opportunities.",
      agency_freelancer: "Growing headcount increases need for external service providers."
    },
    audienceWeights: {
      job_seeker: 10,
      recruiter: 10,
      staffing_agency: 10,
      career_coach: 9,
      workforce_organization: 9,
      college_career_center: 9,
      sales_professional: 6,
      business_development: 7,
      agency_freelancer: 6
    }
  },

  hiring_multiple_roles: {
    key: "hiring_multiple_roles",
    defaultLabel: "Hiring Multiple Roles",
    category: "Hiring and Workforce",
    defaultSource: "Google Maps Listing / Reviews",
    defaultConfidence: "Basic",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Hiring Multiple Roles",
      recruiter: "Multi-Role Recruitment Drive",
      staffing_agency: "Volume Staffing Opportunity",
      career_coach: "Diverse Role Opportunities",
      workforce_organization: "Broad Participant Placement",
      college_career_center: "Multi-Department Opportunities",
      sales_professional: "Rapid Organizational Growth",
      business_development: "Multi-Team Expansion",
      agency_freelancer: "Scaling Operations Signal"
    },
    audienceWeights: {
      job_seeker: 9,
      recruiter: 10,
      staffing_agency: 10,
      career_coach: 8,
      workforce_organization: 9,
      college_career_center: 8,
      sales_professional: 7,
      business_development: 8,
      agency_freelancer: 7
    }
  },

  expanding_location: {
    key: "expanding_location",
    defaultLabel: "Expanding Location",
    category: "Growth and Expansion",
    defaultSource: "Address / Name Analysis",
    defaultConfidence: "Basic",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Expanding Location",
      recruiter: "Employer Expansion",
      staffing_agency: "Growing Employer",
      career_coach: "Expanding Employer",
      workforce_organization: "Regional Employer Growth",
      college_career_center: "Expanding Local Brand",
      sales_professional: "Business Growth Indicator",
      business_development: "Expanding Business Footprint",
      agency_freelancer: "Expanding Enterprise"
    },
    audienceDescriptions: {
      job_seeker: "Business location parameters indicate team or branch growth in your area.",
      recruiter: "Branch or unit expansion driving ongoing local recruitment requirements.",
      staffing_agency: "New branch opening or space expansion requiring rapid local workforce pipeline.",
      sales_professional: "Physical or organizational expansion signals active budget and service needs.",
      business_development: "Multi-branch footprint creates prime co-marketing and strategic alliance opportunities.",
      agency_freelancer: "Expanding footprint requires updated digital assets and marketing support."
    },
    audienceWeights: {
      job_seeker: 8,
      recruiter: 9,
      staffing_agency: 9,
      career_coach: 8,
      workforce_organization: 8,
      college_career_center: 7,
      sales_professional: 9,
      business_development: 10,
      agency_freelancer: 8
    }
  },

  high_hiring_signal: {
    key: "high_hiring_signal",
    defaultLabel: "High Hiring Signal",
    category: "Hiring and Workforce",
    defaultSource: "Category + Reviews",
    defaultConfidence: "Basic",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "High Hiring Signal",
      recruiter: "High Talent Demand",
      staffing_agency: "Urgent Staffing Demand",
      sales_professional: "High Activity Business"
    },
    audienceWeights: {
      job_seeker: 9,
      recruiter: 10,
      staffing_agency: 10,
      sales_professional: 6
    }
  },

  seasonal_hiring_likely: {
    key: "seasonal_hiring_likely",
    defaultLabel: "Seasonal Hiring Likely",
    category: "Hiring and Workforce",
    defaultSource: "Trade Category Analysis",
    defaultConfidence: "Basic",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Seasonal Hiring Likely",
      recruiter: "Cyclic Talent Surge",
      staffing_agency: "Seasonal Temp Staffing Need",
      workforce_organization: "Seasonal Work Program Target"
    },
    audienceWeights: {
      job_seeker: 7,
      recruiter: 8,
      staffing_agency: 9,
      workforce_organization: 8
    }
  },

  repeated_hiring_activity: {
    key: "repeated_hiring_activity",
    defaultLabel: "Repeated Hiring Activity",
    category: "Hiring and Workforce",
    defaultSource: "System Analysis",
    defaultConfidence: "Improved",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Recurring Job Openings",
      recruiter: "Continuous Recruitment Account",
      staffing_agency: "Recurring Staffing Client",
      career_coach: "Regular Hiring Pipeline"
    },
    audienceWeights: {
      job_seeker: 8,
      recruiter: 9,
      staffing_agency: 10,
      career_coach: 8
    }
  },

  internship_indicator: {
    key: "internship_indicator",
    defaultLabel: "Internship Opportunities",
    category: "Hiring and Workforce",
    defaultSource: "Category / System Analysis",
    defaultConfidence: "Improved",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Internship Opportunities",
      college_career_center: "Prime Internship Employer",
      workforce_organization: "Early Career Training Ground"
    },
    audienceWeights: {
      job_seeker: 9,
      college_career_center: 10,
      workforce_organization: 8
    }
  },

  careers_page_found: {
    key: "careers_page_found",
    defaultLabel: "Careers Page Found",
    category: "Hiring and Workforce",
    defaultSource: "Website Review",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Direct Careers Portal",
      recruiter: "Dedicated Jobs Page",
      staffing_agency: "Active Recruitment Channel"
    },
    audienceWeights: {
      job_seeker: 10,
      recruiter: 9,
      staffing_agency: 8
    }
  },

  application_link_available: {
    key: "application_link_available",
    defaultLabel: "Application Link Available",
    category: "Hiring and Workforce",
    defaultSource: "Website Review",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Instant Online Application",
      recruiter: "Digital Application Active"
    },
    audienceWeights: {
      job_seeker: 10,
      recruiter: 8
    }
  },

  // --- 2. Contact and Access ---
  verified_decision_maker_found: {
    key: "verified_decision_maker_found",
    defaultLabel: "Verified Decision-Maker Found",
    category: "Contact and Access",
    defaultSource: "Contact Search / Crawler",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Hiring Manager Contact On-File",
      recruiter: "Hiring Manager Contact Found",
      staffing_agency: "Decision Maker Identified",
      sales_professional: "Direct Decision-Maker Contact",
      business_development: "Executive Contact Found",
      agency_freelancer: "Business Owner Contact Available"
    },
    audienceDescriptions: {
      job_seeker: "Direct contact line for owner, manager, or department head on file.",
      recruiter: "Direct executive or department manager contact details bypass gatekeepers.",
      staffing_agency: "Identified decision-maker with hiring authority for quick outreach.",
      sales_professional: "Direct access to key stakeholder with purchasing decision authority.",
      business_development: "Executive contact for high-level partnership discussions.",
      agency_freelancer: "Owner contact details for direct service pitch and proposals."
    },
    audienceWeights: {
      job_seeker: 8,
      recruiter: 10,
      staffing_agency: 9,
      sales_professional: 10,
      business_development: 10,
      agency_freelancer: 10
    }
  },

  contact_info_available: {
    key: "contact_info_available",
    defaultLabel: "Contact Info Available",
    category: "Contact and Access",
    defaultSource: "Google Maps Listing",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Direct Phone & Contact Line",
      sales_professional: "Verified Contact Channel"
    },
    audienceWeights: {
      job_seeker: 7,
      sales_professional: 8
    }
  },

  easy_to_contact: {
    key: "easy_to_contact",
    defaultLabel: "Easy to Contact",
    category: "Contact and Access",
    defaultSource: "System Analysis",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceWeights: {
      job_seeker: 8,
      sales_professional: 8,
      agency_freelancer: 8
    }
  },

  direct_voice_channel: {
    key: "direct_voice_channel",
    defaultLabel: "Direct Voice Channel",
    category: "Contact and Access",
    defaultSource: "System Analysis",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceWeights: {
      sales_professional: 8,
      job_seeker: 7
    }
  },

  // --- 3. Growth and Expansion ---
  strong_reviews: {
    key: "strong_reviews",
    defaultLabel: "Strong Reviews",
    category: "Reputation and Trust",
    defaultSource: "Google Maps Listing",
    defaultConfidence: "Basic",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Highly Rated Employer",
      career_coach: "Strong Workplace Reputation",
      college_career_center: "Top Rated Business Partner",
      business_development: "Established Market Brand"
    },
    audienceWeights: {
      job_seeker: 8,
      career_coach: 10,
      college_career_center: 9,
      business_development: 9
    }
  },

  high_local_demand: {
    key: "high_local_demand",
    defaultLabel: "High Local Demand",
    category: "Growth and Expansion",
    defaultSource: "Google Maps Listing",
    defaultConfidence: "Basic",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceWeights: {
      job_seeker: 8,
      sales_professional: 8,
      business_development: 9
    }
  },

  multiple_locations: {
    key: "multiple_locations",
    defaultLabel: "Multiple Locations",
    category: "Growth and Expansion",
    defaultSource: "Google Maps Listing",
    defaultConfidence: "Improved",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      recruiter: "Multi-Branch Account",
      staffing_agency: "Multi-Unit Footprint",
      business_development: "Multi-Location Enterprise"
    },
    audienceWeights: {
      recruiter: 9,
      staffing_agency: 9,
      business_development: 10
    }
  },

  // --- 4. Location and Travel ---
  nearby: {
    key: "nearby",
    defaultLabel: "Nearby",
    category: "Location and Travel",
    defaultSource: "Geographic Analysis",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Short Commute Distance",
      career_coach: "Convenient Client Commute",
      college_career_center: "Close to Campus"
    },
    audienceWeights: {
      job_seeker: 10,
      career_coach: 8,
      college_career_center: 9
    }
  },

  immediate_walk_in_range: {
    key: "immediate_walk_in_range",
    defaultLabel: "Immediate Walk-In Range",
    category: "Location and Travel",
    defaultSource: "Geographic Analysis",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceWeights: {
      job_seeker: 10
    }
  },

  sub_5_mile_commute_proximity: {
    key: "sub_5_mile_commute_proximity",
    defaultLabel: "Sub-5 Mile Commute Proximity",
    category: "Location and Travel",
    defaultSource: "Geographic Analysis",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceWeights: {
      job_seeker: 9
    }
  },

  transit_oriented_corridor: {
    key: "transit_oriented_corridor",
    defaultLabel: "Transit-Oriented Corridor",
    category: "Location and Travel",
    defaultSource: "Geographic Analysis",
    defaultConfidence: "Improved",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceWeights: {
      job_seeker: 8,
      workforce_organization: 8
    }
  },

  // --- 5. Website and Digital Presence ---
  website_available: {
    key: "website_available",
    defaultLabel: "Website Available",
    category: "Website and Digital Presence",
    defaultSource: "Google Maps Listing",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceWeights: {
      job_seeker: 7,
      sales_professional: 6,
      agency_freelancer: 6
    }
  },

  no_website_found: {
    key: "no_website_found",
    defaultLabel: "No Website Found",
    category: "Website and Digital Presence",
    defaultSource: "Google Maps Listing",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      sales_professional: "Missing Website (High Sales Lead)",
      agency_freelancer: "Missing Website (New Site Prospect)"
    },
    audienceDescriptions: {
      sales_professional: "Lacks official website listing; prime warm web design and marketing lead.",
      agency_freelancer: "No website present on Google listing; immediate new web development prospect."
    },
    audienceWeights: {
      sales_professional: 10,
      agency_freelancer: 10,
      job_seeker: 3
    }
  },

  website_needs_review: {
    key: "website_needs_review",
    defaultLabel: "Website Needs Review",
    category: "Website and Digital Presence",
    defaultSource: "Website Review",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      sales_professional: "Website Upgrade Opportunity",
      agency_freelancer: "Website Redesign Prospect"
    },
    audienceWeights: {
      sales_professional: 9,
      agency_freelancer: 10
    }
  },

  mobile_friendliness_gap: {
    key: "mobile_friendliness_gap",
    defaultLabel: "Mobile-Friendliness Gap",
    category: "Website and Digital Presence",
    defaultSource: "Website Review",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      sales_professional: "Mobile Optimization Gap",
      agency_freelancer: "Mobile Responsive Upgrade Prospect"
    },
    audienceWeights: {
      sales_professional: 8,
      agency_freelancer: 9
    }
  },

  ssl_security_risk: {
    key: "ssl_security_risk",
    defaultLabel: "SSL Security Risk",
    category: "Website and Digital Presence",
    defaultSource: "Website Review",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      sales_professional: "SSL Security Finding",
      agency_freelancer: "HTTPS Security Upgrade Needed"
    },
    audienceWeights: {
      sales_professional: 8,
      agency_freelancer: 9
    }
  },

  booking_system_gap: {
    key: "booking_system_gap",
    defaultLabel: "Booking System Gap",
    category: "Website and Digital Presence",
    defaultSource: "Website Review",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      sales_professional: "Missing Online Scheduler",
      agency_freelancer: "Booking Integration Opportunity"
    },
    audienceWeights: {
      sales_professional: 8,
      agency_freelancer: 9
    }
  },

  missing_digital_form_risk: {
    key: "missing_digital_form_risk",
    defaultLabel: "Missing Digital Form Risk",
    category: "Website and Digital Presence",
    defaultSource: "Website Review",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      sales_professional: "Lead Capture Gap",
      agency_freelancer: "Form Integration Opportunity"
    },
    audienceWeights: {
      sales_professional: 8,
      agency_freelancer: 9
    }
  },

  // --- 6. AI and Automation ---
  automation_opportunity: {
    key: "automation_opportunity",
    defaultLabel: "Automation Opportunity",
    category: "AI and Automation",
    defaultSource: "System Analysis",
    defaultConfidence: "Improved",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      sales_professional: "Automation Lead",
      agency_freelancer: "AI / Workflow Integration Opportunity"
    },
    audienceWeights: {
      sales_professional: 8,
      agency_freelancer: 9
    }
  },

  known_ai_usage: {
    key: "known_ai_usage",
    defaultLabel: "Known AI Usage",
    category: "AI and Automation",
    defaultSource: "Website Review",
    defaultConfidence: "Improved",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceWeights: {
      sales_professional: 7,
      agency_freelancer: 7
    }
  },

  // --- 7. Warnings and Risks ---
  possibly_closed: {
    key: "possibly_closed",
    defaultLabel: "Possibly Closed",
    category: "Warnings and Risks",
    defaultSource: "Google Maps Listing",
    defaultConfidence: "Basic",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceWeights: {
      job_seeker: -10,
      recruiter: -10,
      sales_professional: -10
    }
  },

  possible_duplicate: {
    key: "possible_duplicate",
    defaultLabel: "Possible Duplicate",
    category: "Warnings and Risks",
    defaultSource: "Google Maps Listing",
    defaultConfidence: "Basic",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceWeights: {
      job_seeker: -5,
      recruiter: -5,
      sales_professional: -5
    }
  },

  low_confidence_data: {
    key: "low_confidence_data",
    defaultLabel: "Low Confidence Data",
    category: "Warnings and Risks",
    defaultSource: "Google Maps Listing",
    defaultConfidence: "Basic",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceWeights: {
      job_seeker: -5,
      recruiter: -5,
      sales_professional: -5
    }
  },

  phone_available: {
    key: "phone_available",
    defaultLabel: "Phone Available",
    category: "Contact and Access",
    defaultSource: "Business Listing",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Direct Phone Line",
      recruiter: "Verified Telephone Channel",
      staffing_agency: "Direct Phone Verified",
      sales_professional: "Direct Phone Line",
      agency_freelancer: "Direct Phone On File"
    },
    audienceWeights: {
      job_seeker: 8,
      recruiter: 8,
      staffing_agency: 9,
      sales_professional: 8,
      agency_freelancer: 8
    }
  },

  easy_application_direct_contact: {
    key: "easy_application_direct_contact",
    defaultLabel: "Easy Application & Direct Contact",
    category: "Contact and Access",
    defaultSource: "Business Listing",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Easy Application & Direct Contact",
      recruiter: "Direct Application Channel",
      staffing_agency: "Direct Access Channel",
      career_coach: "Accessible Application Channel",
      college_career_center: "Direct Student Contact Path"
    },
    audienceWeights: {
      job_seeker: 9,
      recruiter: 8,
      staffing_agency: 8,
      career_coach: 8,
      college_career_center: 8
    }
  },

  distance_proximity: {
    key: "distance_proximity",
    defaultLabel: "Distance Proximity",
    category: "Location and Travel",
    defaultSource: "Geographic Analysis",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: true,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "Commute Proximity",
      career_coach: "Convenient Commute",
      college_career_center: "Campus Proximity"
    },
    audienceWeights: {
      job_seeker: 9,
      career_coach: 8,
      college_career_center: 9
    }
  },

  high_competition: {
    key: "high_competition",
    defaultLabel: "High Competition",
    category: "Reputation and Trust",
    defaultSource: "Brand Recognition & Traffic Analysis",
    defaultConfidence: "Full",
    filterable: true,
    rankingEligible: false,
    cardEligible: true,
    detailEligible: true,
    audienceLabels: {
      job_seeker: "High Competition",
      recruiter: "Well-Known Employer",
      staffing_agency: "Large Recognized Employer",
      career_coach: "High Competition",
      workforce_organization: "Large Recognized Employer",
      college_career_center: "High Competition",
      sales_professional: "Highly Visible Brand",
      business_development: "Major Market Presence",
      agency_freelancer: "Highly Visible Brand"
    },
    audienceDescriptions: {
      job_seeker: "Many people are likely to recognize and contact this business, so competition may be higher.",
      recruiter: "Many people are likely to recognize and contact this business, so competition may be higher.",
      staffing_agency: "Many people are likely to recognize and contact this business, so competition may be higher.",
      career_coach: "Many people are likely to recognize and contact this business, so competition may be higher.",
      workforce_organization: "Many people are likely to recognize and contact this business, so competition may be higher.",
      college_career_center: "Many people are likely to recognize and contact this business, so competition may be higher.",
      sales_professional: "Many people are likely to recognize and contact this business, so competition may be higher.",
      business_development: "Many people are likely to recognize and contact this business, so competition may be higher.",
      agency_freelancer: "Many people are likely to recognize and contact this business, so competition may be higher."
    },
    audienceWeights: {
      job_seeker: 0,
      recruiter: 0,
      staffing_agency: 0,
      career_coach: 0,
      workforce_organization: 0,
      college_career_center: 0,
      sales_professional: 0,
      business_development: 0,
      agency_freelancer: 0
    }
  }
};

/**
 * Mappings from alias strings to canonical keys.
 */
export const SIGNAL_ALIASES: Record<string, string> = {
  "High Competition": "high_competition",
  high_competition: "high_competition",
  "Well-Known Employer": "high_competition",
  "Large Recognized Employer": "high_competition",
  "Highly Visible Brand": "high_competition",
  "Major Market Presence": "high_competition",
  business_expansion: "expanding_location",
  expanding_business_footprint: "expanding_location",
  employer_expansion: "expanding_location",
  "Expanding Location": "expanding_location",
  "Currently Hiring": "currently_hiring",
  "Hiring Multiple Roles": "hiring_multiple_roles",
  "High Hiring Signal": "high_hiring_signal",
  "Seasonal Hiring Likely": "seasonal_hiring_likely",
  "Careers Page Found": "careers_page_found",
  "Application Link Available": "application_link_available",
  "Owner/manager contact found": "verified_decision_maker_found",
  "Verified Decision-Maker Found": "verified_decision_maker_found",
  executive_contact_available: "verified_decision_maker_found",
  "No Website Found": "no_website_found",
  missing_website: "no_website_found",
  "Website Needs Review": "website_needs_review",
  "Mobile-Friendliness Gap": "mobile_friendliness_gap",
  "SSL Security Risk": "ssl_security_risk",
  "Booking System Gap": "booking_system_gap",
  "Missing Digital Form Risk": "missing_digital_form_risk",
  "Strong Reviews": "strong_reviews",
  "Highly Reviewed": "strong_reviews",
  "Strong Local Reputation": "strong_reviews",
  "Phone Available": "phone_available",
  phone_available: "phone_available",
  "Easy Application & Direct Contact": "easy_application_direct_contact",
  "Easy Application": "easy_application_direct_contact",
  "Direct Contact Available": "easy_application_direct_contact",
  "Distance Proximity": "distance_proximity",
  "Close Commute": "distance_proximity",
  "Nearby": "nearby",
  "Immediate Walk-In Range": "immediate_walk_in_range",
  "Sub-5 Mile Commute Proximity": "sub_5_mile_commute_proximity",
  "Public Transit Access": "transit_oriented_corridor",
  "Transit-Oriented Corridor": "transit_oriented_corridor",
  "Walkable Area": "high_walkability_score",
  "High Walkability Score": "high_walkability_score",
  "Multiple Locations": "multiple_locations",
  multi_unit_footprint: "multiple_locations",
  "Automation Opportunity": "automation_opportunity",
  "Known AI Usage": "known_ai_usage",
  "Possibly Closed": "possibly_closed",
  "Possible Duplicate": "possible_duplicate",
  "Low Confidence Data": "low_confidence_data"
};

/**
 * Resolves any string alias or display tag to its canonical key ID.
 */
export function resolveCanonicalKey(inputTag: string): string {
  if (!inputTag) return "general_signal";
  const trimmed = inputTag.trim();
  if (CANONICAL_SIGNALS[trimmed]) return trimmed;
  if (SIGNAL_ALIASES[trimmed]) return SIGNAL_ALIASES[trimmed];

  const lower = trimmed.toLowerCase();
  if (lower.includes("miles away") || lower.includes("mile commute") || lower.includes("commute proximity")) {
    return "distance_proximity";
  }
  if (lower.startsWith("rated ") || lower.includes("star rating") || lower.includes("workplace reputation")) {
    return "strong_reviews";
  }
  if (lower.includes("phone available") || lower.includes("direct phone")) {
    return "phone_available";
  }
  if (lower.includes("easy application") || lower.includes("direct contact")) {
    return "easy_application_direct_contact";
  }

  // Fallback slugification
  const slug = trimmed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (CANONICAL_SIGNALS[slug]) return slug;
  if (SIGNAL_ALIASES[slug]) return SIGNAL_ALIASES[slug];

  return slug || "general_signal";
}

/**
 * Returns all unique tag names and display labels defined in the Intelligence Matrix.
 */
export function getAllIntelligenceMatrixTags(): string[] {
  const tagsSet = new Set<string>();

  Object.values(CANONICAL_SIGNALS).forEach((sig) => {
    if (sig.defaultLabel) tagsSet.add(sig.defaultLabel);
    if (sig.key) {
      // Formatted version of key
      const formattedKey = sig.key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      tagsSet.add(formattedKey);
    }
    if (sig.audienceLabels) {
      Object.values(sig.audienceLabels).forEach((lbl) => {
        if (lbl) tagsSet.add(lbl);
      });
    }
  });

  Object.keys(SIGNAL_ALIASES).forEach((alias) => {
    if (alias) {
      const cleanAlias = alias
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      tagsSet.add(cleanAlias);
    }
  });

  return Array.from(tagsSet).filter(Boolean).sort();
}

